import { SlotButton } from '@avanta/shared/components/SlotButton';
import { Button } from '@avanta/shared/components/ui/Button';
import { Calendar } from '@avanta/shared/components/ui/Calendar';
import { Separator } from '@avanta/shared/components/ui/Separator';
import {
  APPOINTMENTS_SCHEDULE_DAYS,
  LOCALES,
  LOCIZE_NS,
  type Languages,
} from '@avanta/shared/constants/common';
import { useHasVerticalOverflow } from '@avanta/shared/hooks/useHasVerticalOverflow';
import { checkIsDayDisabled, getFirstAvailableDate } from '@avanta/shared/utils/appointments';
import { cn } from '@avanta/shared/utils/cn';
import { extractTimeFromUnified, formatDateTimeUnified } from '@avanta/shared/utils/date';
import { addDays, format, isSameMonth, startOfDay } from 'date-fns';
import { CalendarIcon, ChevronRight, Clock } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AnimatedContainer } from 'components/ui/AnimatedContainer';
import { MOBILE_MAX_WIDTH } from 'constants/common';
import { useWindowWidth } from 'hooks/useWindowWidth';
import { useUserStore } from 'stores/user';
import { AppointmentSteps } from 'types/appointments';
import { CONTAINER_XL_MAX_WIDTH } from '../../../styles';
import { AppointmentsReservePayload, type StepProps } from '../../../types';
import { BackButton } from '../../ui/BackButton';
import { SearchSummary } from '../../ui/SearchSummary';
import { StepSection } from '../../ui/StepSection';
import { SearchDateTimeStepSkeleton } from './SearchDateTimeStepSkeleton';
import { useFetchPsychologistAvailability } from './useFetchPsychologistAvailability';

export const SearchDateTimeStep = ({
  handleNext,
  handleBack,
  nextStepName,
}: StepProps): React.ReactElement => {
  const { setValue, watch } = useFormContext();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const language = useUserStore(state => state.user?.appLanguage);
  const locale = LOCALES[language as Languages];

  const { availableDates, isLoading } = useFetchPsychologistAvailability();
  const dateTime = watch(AppointmentsReservePayload.dateTime);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    getFirstAvailableDate(availableDates)
  );
  const [prevAvailableDates, setPrevAvailableDates] = useState(availableDates);

  if (prevAvailableDates !== availableDates) {
    setPrevAvailableDates(availableDates);
    setSelectedDate(getFirstAvailableDate(availableDates));
  }
  const today = useMemo(() => startOfDay(new Date()), []);
  const endDate = useMemo(() => startOfDay(addDays(today, APPOINTMENTS_SCHEDULE_DAYS)), [today]);
  const selectedTime = dateTime ? extractTimeFromUnified(dateTime) : '';
  const availableTimeSlots = availableDates[format(selectedDate ?? today, 'yyyy-MM-dd')];

  const nextStepLabelDesktop =
    nextStepName === AppointmentSteps.payment ? 'continueWithPayment' : 'continueWithConfirmation';
  const nextStepLabelMobile =
    nextStepName === AppointmentSteps.payment ? 'Steps.continueWithPayment' : 'next';

  const layoutRootRef = useRef<HTMLDivElement>(null);
  const { hasOverflow, isAtBottom } = useHasVerticalOverflow(
    layoutRootRef,
    'nearestScrollParent',
    availableTimeSlots
  );
  const showBottomShadow = hasOverflow && !isAtBottom;
  const width = useWindowWidth().width;
  const isMobile = width < MOBILE_MAX_WIDTH;
  const isStickyFooter = !!dateTime && isMobile;
  const isScrollingFooter = isStickyFooter && !isAtBottom;

  const handleOnSelectDay = (date: Date | undefined): void => {
    setSelectedDate(date);
    setValue(AppointmentsReservePayload.dateTime, '');
  };

  const handleOnSelectTime = (time: string): void => {
    const isDeselecting = time === selectedTime;
    setValue(
      AppointmentsReservePayload.dateTime,
      !isDeselecting && selectedDate ? formatDateTimeUnified(selectedDate, time) : ''
    );
  };

  const isDayDisabled = useCallback(
    (day: Date): boolean => checkIsDayDisabled(day, availableDates, today, endDate),
    [availableDates, today, endDate]
  );

  const handleOnBack = (): void => {
    setSelectedDate(undefined);
    setValue(AppointmentsReservePayload.dateTime, '');
    handleBack?.();
  };

  return (
    <AnimatedContainer
      key={selectedDate?.toString()}
      ref={layoutRootRef}
      className={cn(CONTAINER_XL_MAX_WIDTH, 'flex flex-col gap-8 am-lg:gap-10 pb-4')}
    >
      <div className="flex flex-col am-lg:flex-row gap-8 am-lg:gap-6">
        <SearchSummary showDetails title={t('AppointmentsReserve.Search.DateTime.Summary.title')} />
        {isLoading ? (
          <SearchDateTimeStepSkeleton />
        ) : (
          <div className={cn('flex flex-col gap-8 am-lg:gap-10 transition-opacity w-full')}>
            <StepSection
              title={t('AppointmentsReserve.Search.DateTime.Calendar.availableDates.title')}
              className="grid am-lg:grid-cols-2 gap-4 am-lg:gap-12 p-0 am-lg:p-6 border border-transparent am-lg:border-teal-300 rounded-xl h-full"
              containerClassName="h-full am-lg:gap-2"
            >
              <Calendar
                className="w-full border border-teal-300 am-lg:border-transparent rounded-xl px-2 am-lg:p-0"
                selected={selectedDate}
                defaultMonth={selectedDate}
                startMonth={today}
                endMonth={endDate}
                onSelect={handleOnSelectDay}
                disabled={isDayDisabled}
                showOutsideDays
                {...(isSameMonth(today, endDate) && { hideNavigation: true })}
              />
              <div className="flex flex-col gap-2 am-lg:">
                <p className="text-sm font-medium text-denim-500">
                  {t('AppointmentsReserve.Steps.dateTime.section.schedule.time.title')}
                </p>
                <div className="flex am-lg:flex-col gap-3.5 am-lg:gap-4 flex-col justify-between h-full">
                  {!selectedDate && (
                    <p className="am-lg:text-sm text-xs font-normal leading-4 text-gray-500">
                      {t('AppointmentsReserve.Steps.dateTime.section.schedule.time.noDate')}
                    </p>
                  )}
                  <div className="grid grid-cols-5 am-lg:grid-cols-3 gap-2">
                    {selectedDate &&
                      availableTimeSlots?.map(time => {
                        const timeWithoutSeconds = time.slice(0, 5);
                        return (
                          <SlotButton
                            key={time}
                            onClick={() => handleOnSelectTime(timeWithoutSeconds)}
                            isActive={selectedTime === timeWithoutSeconds}
                          >
                            {timeWithoutSeconds}
                          </SlotButton>
                        );
                      })}
                  </div>
                  {selectedDate && selectedTime && (
                    <div className="flex flex-col gap-2 justify-between items-center am-lg:items-start">
                      <Separator className="bg-teal-300 my-1" />
                      <p className="font-medium text-denim-800 leading-5 flex gap-2 items-center">
                        <CalendarIcon className="size-4 text-teal-600" />
                        {format(selectedDate, 'dd MMMM, yyyy', {
                          locale,
                        })}
                      </p>
                      <p className="text-denim-800 leading-5 flex gap-2 items-center">
                        <Clock className="size-4 text-teal-600" />
                        {selectedTime}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </StepSection>
          </div>
        )}
      </div>
      <div
        className={cn(
          'flex justify-between gap-4',
          isStickyFooter && 'sticky bottom-0 left-0 right-0 bg-white',
          isScrollingFooter && 'h-14 pt-3.5 pb-2',
          showBottomShadow && !!dateTime && 'shadow-mobile-menu-top justify-center -mx-4'
        )}
      >
        {!isScrollingFooter && <BackButton handleBack={handleOnBack} />}
        <Button
          type="button"
          variant="primary"
          className={cn(
            'flex-1 am-lg:w-fit am-lg:flex-none',
            showBottomShadow && !!dateTime && 'w-9/10 flex-none'
          )}
          size={isScrollingFooter ? 'md' : 'lg'}
          icon="trailing"
          onClick={handleNext}
          disabled={!dateTime}
        >
          <span className="am-lg:hidden">{t(`AppointmentsReserve.${nextStepLabelMobile}`)}</span>
          <span className="hidden am-lg:inline">
            {t(`AppointmentsReserve.Steps.${nextStepLabelDesktop}`)}
          </span>
          <ChevronRight className="size-5" />
        </Button>
      </div>
    </AnimatedContainer>
  );
};
